import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip,
  useTheme,
  Divider,
  Paper,
  Checkbox,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RoomIcon from '@mui/icons-material/Room';
import CheckInIcon from '@mui/icons-material/Login';
import CheckOutIcon from '@mui/icons-material/Logout';
import HotelIcon from '@mui/icons-material/Hotel';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SwapHoriz from '@mui/icons-material/SwapHoriz';
import { getRoomAllocationAvailableRooms, assignRooms } from '../../services/api';
import { enrichBookingGuestRef } from '../../utils/bookingDrawerLinks';
import { resolveStayCheckInDisplay, resolveStayCheckOutDisplay, resolveActualCheckInDisplay, resolveActualCheckOutDisplay, isSameDayStay } from '../../utils/bookingStayDisplay';
import ActualStayDateChips from './ActualStayDateChips';
import StayDurationChip from './StayDurationChip';
import { detectThemeSync, watchThemeChanges } from '../../utils/themeUtils';
import moment from 'moment';
import ChannelImage from '../common/ChannelImage';
import {
  getBookingDrawerTheme,
  getBookingDrawerPaperSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerFooterSx,
  getBookingDrawerPanelSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPrimaryButtonSx,
  getBookingDrawerAuditButtonSx,
  getBookingDrawerInputBg,
  getBookingDrawerDividerColor,
  BOOKING_DRAWER_ACCENT,
} from '../../utils/bookingDrawerLayout';
import BookingAuditDrawer from './BookingAuditDrawer';
import BookingDrawerPaymentCard from './BookingDrawerPaymentCard';
import { BookingDrawerHeaderIdentity, DrawerGuestFieldLink } from './DrawerEntityLink';
import { PERMISSIONS } from '../../auth/permissions';
import usePermission from '../../auth/usePermission';
import {
  getCheckInRoomHoldChipColor,
  getCheckInRoomHoldLabel,
  getCheckInRoomHoldOverlaySx,
  getCheckInRoomHoldReason,
  getCheckInRoomHoldTooltip,
} from '../../utils/checkInRoomHold';

const CUSTOM_BORDER_COLOR = '#4ce1b6';

/** Match partial_checkout, "Partial Check-Out", "partial checkout", etc. */
const isPartialCheckoutBookingStatus = (raw) =>
  String(raw ?? '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '') === 'partialcheckout';

const RoomAllocationDrawer = ({
  bookingId,
  open,
  onClose,
  onAssignSuccess,
  mode = 'allocate',
  group = null,
  booking: bookingProp = null,
  onChangeRoomSuccess = null,
  onBookingUpdated = null,
}) => {
  const { can } = usePermission();
  const canViewBookingAudit = can(PERMISSIONS.BOOKING_AUDIT_VIEW);
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';
  const drawerTheme = getBookingDrawerTheme(isDark);

  // Watch for theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [auditDrawerOpen, setAuditDrawerOpen] = useState(false);
  const [availableRooms, setAvailableRooms] = useState(null);
  const [booking, setBooking] = useState(null);
  const [bookingItems, setBookingItems] = useState([]);
  const [selectedRooms, setSelectedRooms] = useState({}); // {booking_item_id: room_id}
  const [selectedRoomIds, setSelectedRoomIds] = useState({}); // {room_type_id: Set(room_ids)} - for quick multi-select
  const [blockedAllocatedRoomIds, setBlockedAllocatedRoomIds] = useState(new Set());
  const auditBookingId = useMemo(() => {
    const parsedId = Number(booking?.id || booking?.booking_id || bookingId);
    return Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null;
  }, [booking?.id, booking?.booking_id, bookingId]);
  const stayCheckInDisplay = useMemo(() => resolveStayCheckInDisplay(booking), [booking]);
  const stayCheckOutDisplay = useMemo(() => resolveStayCheckOutDisplay(booking), [booking]);
  const actualCheckInDisplay = useMemo(() => resolveActualCheckInDisplay(booking), [booking]);
  const actualCheckOutDisplay = useMemo(() => resolveActualCheckOutDisplay(booking), [booking]);

  const isChangeRoomMode = mode === 'changeRoom';
  const isCheckedOutItem = useCallback((item) => {
    const raw = item?.checkout;
    if (typeof raw === 'string') {
      return ['1', 'true', 'yes'].includes(raw.trim().toLowerCase());
    }
    return raw === true || raw === 1;
  }, []);

  const getItemDateKey = useCallback((item) => {
    if (!item?.date) return '';
    return moment.isMoment(item.date)
      ? item.date.format('YYYY-MM-DD')
      : String(item.date).slice(0, 10);
  }, []);

  const pickCurrentSequenceItem = useCallback((items = [], fromDateStr = moment().format('YYYY-MM-DD')) => {
    const sortedItems = [...items].sort((a, b) => {
      const aDate = getItemDateKey(a);
      const bDate = getItemDateKey(b);
      if (aDate !== bDate) return aDate.localeCompare(bDate);
      return (a?.id || 0) - (b?.id || 0);
    });

    return (
      sortedItems.find((it) => {
        const itemDateStr = getItemDateKey(it);
        return !isCheckedOutItem(it) && itemDateStr && itemDateStr >= fromDateStr;
      }) ||
      sortedItems.find((it) => !isCheckedOutItem(it)) ||
      sortedItems.find((it) => {
        const itemDateStr = getItemDateKey(it);
        return itemDateStr && itemDateStr >= fromDateStr;
      }) ||
      sortedItems[0] ||
      null
    );
  }, [getItemDateKey, isCheckedOutItem]);

  // Build allocation groups per room type.
  // Default behavior: same sequence shares one room.
  // Edge case: if same sequence appears multiple times on same date, split per item
  // so each row can have its own room selection.
  const buildAllocationGroups = useCallback((items = []) => {
    const bySequenceDate = new Map();
    items.forEach((item) => {
      const seq = Number(item?.room_sequence || 1);
      const dateKey = getItemDateKey(item);
      const key = `${seq}_${dateKey}`;
      bySequenceDate.set(key, (bySequenceDate.get(key) || 0) + 1);
    });

    const groups = new Map();
    items.forEach((item) => {
      const seq = Number(item?.room_sequence || 1);
      const dateKey = getItemDateKey(item);
      const duplicateSameDateSeq = (bySequenceDate.get(`${seq}_${dateKey}`) || 0) > 1;
      const groupKey = duplicateSameDateSeq ? `item_${item?.id}` : `seq_${seq}`;
      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          key: groupKey,
          sequence: seq,
          dateKey,
          items: [],
        });
      }
      groups.get(groupKey).items.push(item);
    });

    return Array.from(groups.values()).sort((a, b) => {
      if (a.sequence !== b.sequence) return a.sequence - b.sequence;
      if (a.dateKey !== b.dateKey) return String(a.dateKey || '').localeCompare(String(b.dateKey || ''));
      const aId = Number(a.items?.[0]?.id || 0);
      const bId = Number(b.items?.[0]?.id || 0);
      return aId - bId;
    });
  }, [getItemDateKey]);

  // Same rules as fetchAvailableRooms: exclude checked-out booking_item rows for partial checkout
  // (status may be "partial_checkout" or "Partial Checkout" from API).
  const displayBookingItems = useMemo(() => {
    const hasOut = bookingItems.some((it) => isCheckedOutItem(it));
    const restrict =
      isChangeRoomMode ||
      isPartialCheckoutBookingStatus(booking?.status) ||
      hasOut;
    return restrict ? bookingItems.filter((it) => !isCheckedOutItem(it)) : bookingItems;
  }, [bookingItems, booking?.status, isChangeRoomMode, isCheckedOutItem]);

  const displayBookingItemsStreamCount = useMemo(() => {
    const keys = new Set();
    displayBookingItems.forEach((it) => {
      keys.add(`${String(it?.room_type_id ?? '')}_${Number(it?.room_sequence ?? 1)}`);
    });
    return keys.size;
  }, [displayBookingItems]);

  // Fetch available rooms when drawer opens
  useEffect(() => {
    if (open && bookingId) {
      fetchAvailableRooms();
    } else {
      // Reset state when drawer closes
      setAvailableRooms(null);
      setBooking(null);
      setBookingItems([]);
      setSelectedRooms({});
      setSelectedRoomIds({});
      setBlockedAllocatedRoomIds(new Set());
      setError(null);
      setAuditDrawerOpen(false);
    }
  }, [open, bookingId, isChangeRoomMode, group?.key]);

  const fetchAvailableRooms = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getRoomAllocationAvailableRooms(
        bookingId,
        isChangeRoomMode ? { active_only: true } : {}
      );
      let roomTypes = data.room_types || [];
      let items = data.booking_items || [];
      const bookingData = enrichBookingGuestRef(data.booking || bookingProp || {});
      setBooking(bookingData);
      const hasCheckedOutItems = items.some((it) => isCheckedOutItem(it));
      const shouldRestrictToActiveItems =
        isPartialCheckoutBookingStatus(bookingData?.status) ||
        isChangeRoomMode ||
        hasCheckedOutItems;
      if (shouldRestrictToActiveItems) {
        // Never allow allocation/change-room to operate on checked-out rows.
        items = items.filter((it) => !isCheckedOutItem(it));
        const activeTypeIds = new Set(
          items.map((it) => Number(it?.room_type_id || it?.room_type?.id || 0)).filter(Boolean)
        );
        roomTypes = roomTypes.filter((rt) =>
          activeTypeIds.has(Number(rt?.room_type?.id ?? rt?.id ?? 0))
        );
      }
      if (isChangeRoomMode && group?.items?.length) {
        const activeGroupItems = (group.items || []).filter((it) => !isCheckedOutItem(it));
        const groupItemIds = new Set(activeGroupItems.map((i) => i.id).filter(Boolean));
        items = items.filter((it) => groupItemIds.has(it.id));
        const scopedTypeSource = items.length > 0 ? items : activeGroupItems;
        const scopedTypeIds = new Set(
          scopedTypeSource
            .map((it) => Number(it?.room_type_id || it?.room_type?.id || 0))
            .filter(Boolean)
        );
        if (scopedTypeIds.size > 0) {
          roomTypes = roomTypes.filter((rt) =>
            scopedTypeIds.has(Number(rt?.room_type?.id ?? rt?.id ?? 0))
          );
        }
      }
      if (isChangeRoomMode) {
        const scopeItemIds = new Set((items || []).map((it) => it?.id).filter(Boolean));
        const activeBookingItems = (data.booking_items || []).filter((it) => !isCheckedOutItem(it));
        // Any other still-active stream holds its room — including last night on
        // checkout morning — so the same room cannot be selected twice.
        const blockedIds = new Set(
          activeBookingItems
            .filter((it) => !scopeItemIds.has(it?.id))
            .map((it) => Number(it?.allocated_room_id || it?.allocated_room?.id || 0))
            .filter(Boolean)
        );
        setBlockedAllocatedRoomIds(blockedIds);
      } else {
        setBlockedAllocatedRoomIds(new Set());
      }
      setAvailableRooms(roomTypes);
      setBookingItems(items);

      // Auto-select rooms already allocated in booking items.
      // For same-date duplicate rows (same seq/type/date), prefill each row separately.
      const preSelected = {}; // {booking_item_id: room_id}
      const roomIdsState = {}; // {room_type_id: [room_id|null, ...]}
      const itemsToMap = items || [];
      const roomTypesForCheck = isChangeRoomMode && group?.items?.length ? roomTypes : (data.room_types || []);
      const roomTypeIds = [...new Set(itemsToMap.map((item) => Number(item?.room_type_id || 0)).filter(Boolean))];

      roomTypeIds.forEach((roomTypeId) => {
        const itemsForType = itemsToMap.filter((item) => Number(item?.room_type_id || 0) === roomTypeId);
        const allocationGroups = buildAllocationGroups(itemsForType);
        if (allocationGroups.length === 0) return;

        const roomTypeGroup = roomTypesForCheck.find(
          (rt) => Number(rt?.room_type?.id ?? rt?.id) === Number(roomTypeId)
        );
        const selectedForType = [];

        allocationGroups.forEach((groupEntry) => {
          const sortedGroupItems = [...(groupEntry.items || [])].sort((a, b) => {
            const aDate = getItemDateKey(a);
            const bDate = getItemDateKey(b);
            if (aDate !== bDate) return aDate.localeCompare(bDate);
            return (a?.id || 0) - (b?.id || 0);
          });
          const todayStr = moment().format('YYYY-MM-DD');
          const preferredItemWithRoom = isChangeRoomMode
            ? (
              sortedGroupItems.find((it) => getItemDateKey(it) === todayStr && it?.allocated_room_id) ||
              sortedGroupItems.find((it) => getItemDateKey(it) >= todayStr && it?.allocated_room_id) ||
              [...sortedGroupItems].reverse().find((it) => it?.allocated_room_id) ||
              sortedGroupItems.find((it) => it?.allocated_room_id)
            )
            : sortedGroupItems.find((it) => it?.allocated_room_id);
          const allocatedRoomId = preferredItemWithRoom?.allocated_room_id || null;
          const allocatedRoom = allocatedRoomId && roomTypeGroup
            ? (roomTypeGroup.rooms || []).find((room) => room.id === allocatedRoomId)
            : null;
          const roomHeldForAssign = Boolean(getCheckInRoomHoldReason(allocatedRoom));
          const roomIdForGroup = allocatedRoom && !roomHeldForAssign ? allocatedRoomId : null;

          // Keep index alignment with allocationGroups (including null placeholders)
          // so each group keeps its own slot in selectedRoomIds.
          selectedForType.push(roomIdForGroup);
          (groupEntry.items || []).forEach((it) => {
            preSelected[it.id] = roomIdForGroup;
          });
        });

        roomIdsState[String(roomTypeId)] = selectedForType;
      });

      // Keep default selections consistent across allocation/change-room modes:
      // each allocation group starts from its current mapped room (if any).
      setSelectedRooms(preSelected);
      setSelectedRoomIds(roomIdsState);
    } catch (err) {setError('Failed to load available rooms. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRoomToggle = useCallback((roomId, roomTypeId) => {
    setSelectedRoomIds((prev) => {
      const newSelection = { ...prev };
      const roomArray = [...(newSelection[roomTypeId] || [])];
      const index = roomArray.indexOf(roomId);
      if (index > -1) {
        newSelection[roomTypeId] = roomArray.filter(id => id !== roomId);
      } else {
        // If "Not Assigned" is selected, replace it with the selected room.
        const withoutNull = roomArray.filter(id => id !== null);
        newSelection[roomTypeId] = [...withoutNull, roomId];
      }
      return newSelection;
    });
  }, []);

  // Sync selectedRoomIds with selectedRooms - assign rooms to booking items by room-type allocation groups.
  // Normal case: one group per sequence.
  // Duplicate same-date rows: split per item so each row can have a separate room.
  useEffect(() => {
    if (!displayBookingItems.length) {
      setSelectedRooms({});
      return;
    }

    const newSelectedRooms = {};

    const roomTypeIds = [...new Set(displayBookingItems.map((item) => String(item.room_type_id)))];
    roomTypeIds.forEach((roomTypeId) => {
      const selectedIds = selectedRoomIds[roomTypeId] || [];
      
      const itemsForThisType = displayBookingItems.filter(
        (item) => String(item.room_type_id) === roomTypeId
      );
      if (itemsForThisType.length === 0) return;

      const allocationGroups = buildAllocationGroups(itemsForThisType);
      allocationGroups.forEach((groupEntry, groupIndex) => {
        const roomId = groupIndex < selectedIds.length ? selectedIds[groupIndex] : null;
        // Always keep explicit mapping (including null) so unallocation can be submitted.
        (groupEntry.items || []).forEach((item) => {
          newSelectedRooms[item.id] = roomId;
        });
      });
    });
    setSelectedRooms(newSelectedRooms);
  }, [selectedRoomIds, displayBookingItems, buildAllocationGroups]);

  const handleAssignRooms = async () => {
    if (!bookingId) return;

    if (isChangeRoomMode) {
      const todayStr = moment().format('YYYY-MM-DD');
      const streamAssignments = [];
      const changeModeItems = bookingItems.filter((item) => !isCheckedOutItem(item));
      const roomTypeIds = [...new Set(changeModeItems.map((item) => String(item.room_type_id)))];
      roomTypeIds.forEach((roomTypeId) => {
        const selectedIds = selectedRoomIds[roomTypeId] || [];
        const itemsForThisType = changeModeItems.filter((item) => String(item.room_type_id) === String(roomTypeId));
        const allocationGroups = buildAllocationGroups(itemsForThisType);
        allocationGroups.forEach((groupEntry, groupIndex) => {
          const roomId = groupIndex < selectedIds.length ? selectedIds[groupIndex] : null;
          const targetItem = pickCurrentSequenceItem(groupEntry.items || [], todayStr) || groupEntry.items?.[0] || null;
          if (!targetItem) return;
          if (isCheckedOutItem(targetItem)) return;
          const currentRoomId = targetItem?.allocated_room_id ?? targetItem?.allocated_room?.id ?? null;
          const selectedRoomId = roomId ?? null;
          // Only apply change-room to streams whose room selection actually changed.
          // This prevents checkout-marking past dates on untouched streams.
          if (selectedRoomId === currentRoomId) return;
          streamAssignments.push({
            newRoomId: selectedRoomId,
            targetItemId: targetItem?.id || null,
            targetRoomTypeId: targetItem?.room_type_id ?? targetItem?.room_type?.id ?? null,
            targetSequence: targetItem?.room_sequence ?? 1,
          });
        });
      });

      if (!streamAssignments.length) {
        setError('No room changes detected. Please change at least one active stream.');
        return;
      }

      const fromStr = moment().format('YYYY-MM-DD');
      for (const assignment of streamAssignments) {
        const roomTypeId = assignment?.targetRoomTypeId ? String(assignment.targetRoomTypeId) : null;
        const roomTypeGroup = (availableRooms || []).find(
          (rt) => String(rt?.room_type?.id) === roomTypeId
        );
        const selectedRoom = (roomTypeGroup?.rooms || []).find((r) => r.id === assignment.newRoomId);
        if (assignment?.newRoomId && blockedAllocatedRoomIds.has(Number(assignment.newRoomId))) {
          setError('Selected room is already allocated for active booking dates. Please choose another room.');
          return;
        }
        if (selectedRoom) {
          const holdReason = getCheckInRoomHoldReason(selectedRoom);
          if (holdReason) {
            setError(
              getCheckInRoomHoldTooltip(holdReason, selectedRoom.room_number, 'allocation')
              || 'Selected room is not available. Please choose another room.'
            );
            return;
          }
          if (selectedRoom.is_available === false) {
            setError('Selected room is already allocated for active booking dates. Please choose another room.');
            return;
          }
        }
      }
      if (!onChangeRoomSuccess) {
        setError('Change room action is not available.');
        return;
      }
      setSubmitting(true);
      setError(null);
      try {
        await onChangeRoomSuccess({
          newRoomId: streamAssignments[0]?.newRoomId ?? null,
          changeFromDate: fromStr,
          group,
          targetItemId: streamAssignments[0]?.targetItemId || null,
          targetRoomTypeId: streamAssignments[0]?.targetRoomTypeId ?? null,
          targetSequence: streamAssignments[0]?.targetSequence ?? 1,
          streamAssignments,
        });
        onClose();
      } catch (err) {
        const errorMsg = err.response?.data?.error || err.message || 'Failed to change room.';
        setError(errorMsg);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const roomAllocations = {};
      const roomTypeIds = [...new Set(displayBookingItems.map((item) => String(item.room_type_id)))];
      roomTypeIds.forEach((roomTypeId) => {
        const selectedIds = (selectedRoomIds[roomTypeId] || []).filter((roomId) => roomId != null);
        const itemsForThisType = displayBookingItems.filter(
          (item) => String(item.room_type_id) === String(roomTypeId)
        );
        const allocationGroups = buildAllocationGroups(itemsForThisType);
        const usedRoomIds = new Set();
        const pendingGroups = [];

        allocationGroups.forEach((groupEntry) => {
          const itemsForGroup = groupEntry.items || [];
          const currentRoomId = itemsForGroup
            .map((item) => item?.allocated_room_id ?? item?.allocated_room?.id ?? null)
            .find((roomId) => roomId != null && roomId !== '');
          const keepExistingRoomId = selectedIds.find(
            (selectedId) => Number(selectedId) === Number(currentRoomId)
          );

          if (keepExistingRoomId != null && !usedRoomIds.has(Number(keepExistingRoomId))) {
            usedRoomIds.add(Number(keepExistingRoomId));
            itemsForGroup.forEach((item) => {
              roomAllocations[item.id] = keepExistingRoomId;
            });
            return;
          }

          pendingGroups.push(itemsForGroup);
        });

        pendingGroups.forEach((itemsForGroup) => {
          const nextRoomId = selectedIds.find((roomId) => !usedRoomIds.has(Number(roomId)));
          if (nextRoomId == null) {
            itemsForGroup.forEach((item) => {
              roomAllocations[item.id] = null;
            });
            return;
          }
          usedRoomIds.add(Number(nextRoomId));
          itemsForGroup.forEach((item) => {
            roomAllocations[item.id] = nextRoomId;
          });
        });
      });

      const heldSelection = (availableRooms || []).flatMap((roomTypeGroup) =>
        (roomTypeGroup.rooms || []).filter((room) =>
          Object.values(roomAllocations).some((roomId) => Number(roomId) === Number(room.id))
          && getCheckInRoomHoldReason(room)
        )
      )[0];
      if (heldSelection) {
        const holdReason = getCheckInRoomHoldReason(heldSelection);
        setError(
          getCheckInRoomHoldTooltip(holdReason, heldSelection.room_number, 'allocation')
          || 'Selected room is not available. Please choose another room.'
        );
        setSubmitting(false);
        return;
      }

      const result = await assignRooms(bookingId, roomAllocations);
      if (result.success) {
        if (onAssignSuccess) {
          onAssignSuccess(result.booking);
        }
        onClose();
      }
    } catch (err) {const errorMsg = err.response?.data?.error || err.message || 'Failed to assign rooms. Please try again.';
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Group booking items by room type
  const itemsByRoomType = {};
  displayBookingItems.forEach((item) => {
    const roomTypeId = item.room_type_id;
    if (!itemsByRoomType[roomTypeId]) {
      itemsByRoomType[roomTypeId] = [];
    }
    itemsByRoomType[roomTypeId].push(item);
  });

  // Calculate rooms needed per room type
  const roomsNeededByType = {};
  Object.keys(itemsByRoomType).forEach((roomTypeId) => {
    const items = itemsByRoomType[roomTypeId];
    const allocationGroups = buildAllocationGroups(items);
    roomsNeededByType[roomTypeId] = allocationGroups.length;
  });

  // Count selected rooms per type from selectedRoomIds
  const selectedRoomsByType = {};
  Object.keys(selectedRoomIds).forEach((roomTypeId) => {
    selectedRoomsByType[roomTypeId] = (selectedRoomIds[roomTypeId] || []).filter((id) => id != null).length;
  });

  // Check if all required rooms are selected
  let allRoomsSelected = true;
  Object.keys(roomsNeededByType).forEach((roomTypeId) => {
    const needed = roomsNeededByType[roomTypeId];
    const selected = selectedRoomsByType[roomTypeId] || 0;
    if (selected < needed) {
      allRoomsSelected = false;
    }
  });
  const hasChangedRoomSelection = isChangeRoomMode
    ? (() => {
      const todayStr = moment().format('YYYY-MM-DD');
      const roomTypeIds = [...new Set(displayBookingItems.map((item) => String(item.room_type_id)))];
      for (const roomTypeId of roomTypeIds) {
        const selectedIds = selectedRoomIds[roomTypeId] || [];
        const itemsForThisType = displayBookingItems.filter((item) => String(item.room_type_id) === String(roomTypeId));
        const allocationGroups = buildAllocationGroups(itemsForThisType);
        for (let groupIndex = 0; groupIndex < allocationGroups.length; groupIndex += 1) {
          const groupEntry = allocationGroups[groupIndex];
          const selectedRoomId = groupIndex < selectedIds.length ? selectedIds[groupIndex] : null;
          const targetItem =
            pickCurrentSequenceItem(groupEntry.items || [], todayStr) ||
            groupEntry.items?.[0] ||
            null;
          if (!targetItem) continue;
          const currentRoomId = targetItem?.allocated_room_id ?? targetItem?.allocated_room?.id ?? null;
          if (selectedRoomId !== currentRoomId) return true;
        }
      }
      return false;
    })()
    : false;
  let hasSelectedRooms = isChangeRoomMode
    ? (allRoomsSelected && hasChangedRoomSelection)
    : Object.values(selectedRooms).some((roomId) => roomId != null);
  const hasUnallocationChange = !isChangeRoomMode && displayBookingItems.some((item) => {
    if (!Object.prototype.hasOwnProperty.call(selectedRooms, item.id)) return false;
    const nextRoomId = selectedRooms[item.id];
    const prevRoomId = item?.allocated_room_id ?? item?.allocated_room?.id ?? null;
    return nextRoomId == null && prevRoomId != null;
  });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 600 },
          backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 1,
            p: 1.5,
            borderBottom: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
            backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
          }}
        >
          <BookingDrawerHeaderIdentity
            icon={isChangeRoomMode
              ? <SwapHoriz sx={{ color: CUSTOM_BORDER_COLOR, fontSize: 24, flexShrink: 0 }} />
              : <AssignmentIcon sx={{ color: CUSTOM_BORDER_COLOR, fontSize: 24, flexShrink: 0 }} />}
            title={isChangeRoomMode ? 'Change Room' : 'Room Allocation'}
            booking={booking}
            bookingId={bookingId}
            onNavigate={onClose}
            isDark={isDark}
            drawerTheme={drawerTheme}
            label={booking?.code}
          />
          {canViewBookingAudit && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<FactCheckIcon sx={{ fontSize: 16 }} />}
            onClick={() => setAuditDrawerOpen(true)}
            disabled={!auditBookingId}
            sx={{
              borderColor: isDark ? 'rgba(76, 225, 182, 0.35)' : 'rgba(76, 225, 182, 0.45)',
              color: CUSTOM_BORDER_COLOR,
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '999px',
              px: 1.5,
              py: 0.3,
              minWidth: 84,
              '&:hover': {
                borderColor: CUSTOM_BORDER_COLOR,
                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.12)' : 'rgba(76, 225, 182, 0.08)',
              },
              '&.Mui-disabled': {
                borderColor: isDark ? drawerTheme.surfaceBorder : '#d0d0d0',
                color: isDark ? drawerTheme.subtleText : '#999',
              },
            }}
          >
            Audit
          </Button>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}


          {!loading && booking && (
            <>
              {/* Booking Information - Matching CheckInDrawer */}
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  mb: 1.5,
                  backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                  border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                  borderRadius: '12px',
                  position: 'relative',
                }}
              >
                {/* Channel Image and Booking Status - Top Right */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {/* Channel Image - Display only if channel is not null */}
                  {(booking?.channel_data || booking?.channel) && (
                    booking?.channel_data ? (
                      <ChannelImage channel={booking.channel_data} size={32} />
                    ) : (
                      <ChannelImage isPMS={true} size={32} />
                    )
                  )}
                  
                  {/* Booking Status Chip */}
                  {booking.status && (
                    <Chip
                      label={booking.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        backgroundColor: (() => {
                          const statusLower = (booking.status || '').toLowerCase();
                          if (statusLower === 'confirmed') {
                            return isDark ? 'rgba(33, 150, 243, 0.15)' : 'rgba(33, 150, 243, 0.1)';
                          } else if (statusLower === 'checked_in' || statusLower === 'partial_checkin') {
                            return isDark ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.1)';
                          } else if (statusLower === 'checked_out' || statusLower === 'partial_checkout') {
                            return isDark ? 'rgba(158, 158, 158, 0.15)' : 'rgba(158, 158, 158, 0.1)';
                          } else if (statusLower === 'cancelled' || statusLower === 'no_show') {
                            return isDark ? 'rgba(244, 67, 54, 0.15)' : 'rgba(244, 67, 54, 0.1)';
                          } else if (['blocked', 'reserved', 'block'].includes(statusLower)) {
                            return isDark ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.1)';
                          }
                          return isDark ? 'rgba(158, 158, 158, 0.15)' : 'rgba(158, 158, 158, 0.1)';
                        })(),
                        color: (() => {
                          const statusLower = (booking.status || '').toLowerCase();
                          if (statusLower === 'confirmed') {
                            return isDark ? '#64b5f6' : '#1976d2';
                          } else if (statusLower === 'checked_in' || statusLower === 'partial_checkin') {
                            return isDark ? '#81c784' : '#2e7d32';
                          } else if (statusLower === 'checked_out' || statusLower === 'partial_checkout') {
                            return isDark ? '#bdbdbd' : '#616161';
                          } else if (statusLower === 'cancelled' || statusLower === 'no_show') {
                            return isDark ? '#ef5350' : '#c62828';
                          } else if (['blocked', 'reserved', 'block'].includes(statusLower)) {
                            return isDark ? '#ffb74d' : '#e65100';
                          }
                          return isDark ? '#bdbdbd' : '#616161';
                        })(),
                        border: (() => {
                          const statusLower = (booking.status || '').toLowerCase();
                          if (statusLower === 'confirmed') {
                            return `1px solid ${isDark ? 'rgba(33, 150, 243, 0.3)' : 'rgba(33, 150, 243, 0.25)'}`;
                          } else if (statusLower === 'checked_in' || statusLower === 'partial_checkin') {
                            return `1px solid ${isDark ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.25)'}`;
                          } else if (statusLower === 'checked_out' || statusLower === 'partial_checkout') {
                            return `1px solid ${isDark ? 'rgba(158, 158, 158, 0.3)' : 'rgba(158, 158, 158, 0.25)'}`;
                          } else if (statusLower === 'cancelled' || statusLower === 'no_show') {
                            return `1px solid ${isDark ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.25)'}`;
                          } else if (['blocked', 'reserved', 'block'].includes(statusLower)) {
                            return `1px solid ${isDark ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.25)'}`;
                          }
                          return `1px solid ${isDark ? 'rgba(158, 158, 158, 0.3)' : 'rgba(158, 158, 158, 0.25)'}`;
                        })(),
                        backdropFilter: 'blur(10px)',
                        boxShadow: isDark 
                          ? '0 2px 8px rgba(0, 0, 0, 0.3)' 
                          : '0 2px 8px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                  )}
                  {isSameDayStay(booking) && (
                    <Chip
                      label="Hourly"
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        backgroundColor: isDark ? 'rgba(255, 167, 38, 0.2)' : 'rgba(255, 167, 38, 0.12)',
                        color: isDark ? '#ffb74d' : '#ef6c00',
                        border: `1px solid ${isDark ? 'rgba(255, 183, 77, 0.45)' : 'rgba(239, 108, 0, 0.35)'}`,
                      }}
                    />
                  )}
                </Box>
                <Grid container spacing={1.5}>
                  {/* Guest Name with Booking Source */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem' }}>
                          Guest Name:
                        </Typography>
                        <DrawerGuestFieldLink
                          isDark={isDark}
                          booking={booking}
                          guest={booking.guest}
                          guestId={booking?.guest_id ?? booking?.guest?.id}
                          variant="body2"
                          onNavigate={onClose}
                          sx={{ fontWeight: 600, fontSize: '0.85rem' }}
                        >
                          {booking.guest_name || '—'}
                        </DrawerGuestFieldLink>
                      </Box>
                    </Box>
                  </Grid>

                  {/* Mobile Number */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem' }}>
                        Mobile Number:
                      </Typography>
                      <DrawerGuestFieldLink
                        isDark={isDark}
                        booking={booking}
                        guest={booking.guest}
                        guestId={booking?.guest_id ?? booking?.guest?.id}
                        variant="body2"
                        onNavigate={onClose}
                        sx={{ fontWeight: 500, fontSize: '0.85rem' }}
                      >
                        {booking.guest_phone || booking.phone || '—'}
                      </DrawerGuestFieldLink>
                    </Box>
                  </Grid>

                  {/* Check-in Information */}
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: '8px',
                        background: isDark ? 'rgba(76, 225, 182, 0.08)' : 'rgba(76, 225, 182, 0.05)',
                        border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                        minHeight: booking.check_in_time ? 'auto' : '72px',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <CheckInIcon sx={{ fontSize: 12, color: CUSTOM_BORDER_COLOR }} />
                        <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.65rem', fontWeight: 600 }}>
                          Check-In
                </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? '#fff' : '#212121', fontSize: '0.75rem', mb: 0.25 }}>
                        {stayCheckInDisplay.date || '—'}
                      </Typography>
                      {stayCheckInDisplay.time ? (
                        <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.65rem' }}>
                          {stayCheckInDisplay.time}
                        </Typography>
                      ) : (
                        <Box sx={{ height: '16px' }} />
                      )}
                    </Box>
                    <ActualStayDateChips actual={actualCheckInDisplay} label="Actual check-in" isDark={isDark} />
                  </Grid>

                  {/* Check-out Information */}
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: '8px',
                        background: isDark ? 'rgba(76, 225, 182, 0.08)' : 'rgba(76, 225, 182, 0.05)',
                        border: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                        minHeight: (booking.check_out_time_display || booking.check_out_time) ? 'auto' : '72px',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <CheckOutIcon sx={{ fontSize: 12, color: CUSTOM_BORDER_COLOR }} />
                        <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.65rem', fontWeight: 600 }}>
                          Check-Out
                </Typography>
              </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? '#fff' : '#212121', fontSize: '0.75rem', mb: 0.25 }}>
                        {stayCheckOutDisplay.date || '—'}
                      </Typography>
                      {stayCheckOutDisplay.time ? (
                        <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.65rem' }}>
                          {stayCheckOutDisplay.time}
                        </Typography>
                      ) : (
                        <Box sx={{ height: '16px' }} />
                      )}
                    </Box>
                    <ActualStayDateChips actual={actualCheckOutDisplay} label="Actual check-out" isDark={isDark} />
                  </Grid>

                  {/* Nights / Hourly and Rooms */}
                  <Grid item xs={6}>
                    <StayDurationChip
                      booking={booking}
                      isDark={isDark}
                      accentColor={CUSTOM_BORDER_COLOR}
                      captionSx={{ color: isDark ? drawerTheme.subtleText : '#666' }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#666', fontSize: '0.7rem' }}>
                        Number of Rooms:
                      </Typography>
                      <Chip
                        icon={<HotelIcon sx={{ fontSize: 12, color: CUSTOM_BORDER_COLOR }} />}
                        label={
                          displayBookingItems.length > 0
                            ? displayBookingItemsStreamCount
                            : booking.total_rooms || booking.rooms || 0
                        }
                        size="small"
                        sx={{
                          backgroundColor: isDark ? 'rgba(76, 225, 182, 0.15)' : 'rgba(76, 225, 182, 0.1)',
                          color: CUSTOM_BORDER_COLOR,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          height: 22,
                          border: `1px solid ${CUSTOM_BORDER_COLOR}40`,
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              <BookingDrawerPaymentCard
                booking={booking}
                isDark={isDark}
                drawerTheme={drawerTheme}
                accentColor={CUSTOM_BORDER_COLOR}
                onRestaurantPaymentsChanged={async () => {
                  await fetchAvailableRooms();
                  onBookingUpdated?.(bookingId);
                }}
                clearMessage="All payments have been collected for this booking."
              />

              {/* Available Rooms by Type */}
              {availableRooms && availableRooms.length > 0 ? (
                <Box>
                  <Typography variant="h6" sx={{ color: isDark ? drawerTheme.text : '#333', mb: 2, fontWeight: 600 }}>
                    {isChangeRoomMode ? 'Select new room' : 'Select Rooms to Assign'}
                  </Typography>
                  {availableRooms.map((roomTypeGroup) => {
                    const roomTypeId = String(roomTypeGroup.room_type.id);
                    const itemsForThisType = itemsByRoomType[roomTypeId] || [];
                    const roomsNeededForType = roomsNeededByType[roomTypeId] || 0;
                    const selectedForThisType = selectedRoomsByType[roomTypeId] || 0;
                    const isTypeComplete = roomsNeededForType > 0 && selectedForThisType === roomsNeededForType;
                    const selectedRoomArray = selectedRoomIds[roomTypeId] || [];
                    const canSelectMore = selectedForThisType < roomsNeededForType;

                    return (
                      <Box key={roomTypeGroup.room_type.id} sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              color: isDark ? drawerTheme.text : '#333',
                              fontWeight: 500,
                            }}
                          >
                            {roomTypeGroup.room_type.name}
                          </Typography>
                          {roomsNeededForType > 0 && (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <Chip
                                label={`${selectedForThisType}/${roomsNeededForType} room${roomsNeededForType > 1 ? 's' : ''} selected`}
                                size="small"
                                sx={{
                                  backgroundColor: isTypeComplete 
                                    ? (isDark ? '#2e7d32' : '#4caf50')
                                    : (isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5'),
                                  color: isTypeComplete 
                                    ? '#fff'
                                    : (isDark ? drawerTheme.text : '#333'),
                                  fontSize: '0.7rem',
                                  fontWeight: isTypeComplete ? 600 : 400,
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                        <Grid container spacing={2}>
                          {(roomTypeGroup.rooms || []).map((room) => {
                            const isRoomSelected = selectedRoomArray.includes(room.id);
                            const holdReason = getCheckInRoomHoldReason(room);
                            const holdLabel = getCheckInRoomHoldLabel(holdReason);
                            const holdTooltip = getCheckInRoomHoldTooltip(
                              holdReason,
                              room.room_number,
                              'allocation',
                            );
                            const isAllocatedElsewhere = room.is_available === false;
                            const isAllocatedInOtherActiveStream =
                              isChangeRoomMode && blockedAllocatedRoomIds.has(Number(room.id));
                            const isAllocatedForSelection = isAllocatedElsewhere || isAllocatedInOtherActiveStream;
                            const isDisabled =
                              Boolean(holdReason)
                              || isAllocatedInOtherActiveStream
                              || (isAllocatedForSelection && !isRoomSelected)
                              || (!isRoomSelected && !canSelectMore);

                            return (
                              <Grid 
                                item 
                                xs={6} 
                                sm={4} 
                                md={3}
                                key={room.id}
                              >
                                <Card
                                  elevation={1}
                                  onClick={() => {
                                    if (isDisabled) return;
                                    handleRoomToggle(room.id, roomTypeId);
                                  }}
                                  sx={{
                                    backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                                    borderRadius: 3,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    cursor: !isDisabled ? 'pointer' : 'not-allowed',
                                    opacity: isDisabled ? 0.55 : 1,
                                    '&:hover': !isDisabled ? {
                                      transform: 'translateY(-2px)',
                                      boxShadow: isDark
                                        ? '0 8px 16px rgba(76, 225, 182, 0.3)'
                                        : '0 8px 16px rgba(76, 225, 182, 0.2)',
                                    } : {},
                                  }}
                                >
                                  <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                                    {/* Compact layout matching CheckInDrawer confirmed booking design */}
                                    <Box sx={{ position: 'relative', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
                                      {/* Room Type Image - Full Height with Overlay */}
                                      <Box
                                        sx={{
                                          flex: 1,
                                          minHeight: 120,
                                          position: 'relative',
                                          backgroundImage: (roomTypeGroup.room_type.image || roomTypeGroup.room_type.image_url) 
                                            ? `url(${roomTypeGroup.room_type.image || roomTypeGroup.room_type.image_url})`
                                            : `linear-gradient(135deg, ${isDark ? '#3a3a4a' : '#e8e8e8'} 0%, ${isDark ? '#2a2a3a' : '#d8d8d8'} 100%)`,
                                          backgroundColor: isDark ? '#3a3a4a' : '#e8e8e8',
                                          backgroundSize: 'cover',
                                          backgroundPosition: 'center',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          opacity: isDisabled ? 0.5 : 1,
                                        }}
                                      >
                                        {!(roomTypeGroup.room_type.image || roomTypeGroup.room_type.image_url) && (
                                          <RoomIcon sx={{ fontSize: 32, color: isDark ? drawerTheme.subtleText : '#999', opacity: 0.5 }} />
                                        )}
                                        
                                        {/* Gradient Fade Overlay at Top for Readability */}
                                        <Box
                                          sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            height: '50%',
                                            background: `linear-gradient(180deg, 
                                              ${isDark ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.6)'} 0%, 
                                              ${isDark ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.4)'} 50%, 
                                              transparent 100%)`,
                                            pointerEvents: 'none',
                                          }}
                                        />
                                        
                                        {/* Checkbox and Room Number Overlay - Top Left */}
                                        <Box
                                          sx={{
                                            position: 'absolute',
                                            top: 8,
                                            left: 8,
                                            right: 8,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            zIndex: 2,
                                          }}
                                        >
                                          <Checkbox
                                            checked={isRoomSelected}
                                            disabled={isDisabled}
                                            size="small"
                                            sx={{
                                              color: '#fff',
                                              backgroundColor: isRoomSelected 
                                                ? 'rgba(76, 225, 182, 0.9)' 
                                                : 'rgba(0, 0, 0, 0.4)',
                                              borderRadius: '4px',
                                              padding: '2px',
                                              backdropFilter: 'blur(8px)',
                                              '&.Mui-checked': {
                                                color: '#000',
                                                backgroundColor: 'rgba(76, 225, 182, 0.9)',
                                              },
                                              '&:hover': {
                                                backgroundColor: isRoomSelected 
                                                  ? 'rgba(76, 225, 182, 1)' 
                                                  : 'rgba(0, 0, 0, 0.6)',
                                              },
                                              '& .MuiSvgIcon-root': {
                                                fontSize: 20,
                                                filter: isRoomSelected ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' : 'none',
                                              },
                                              transition: 'all 0.3s ease',
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (!isDisabled) {
                                                handleRoomToggle(room.id, roomTypeId);
                                              }
                                            }}
                                          />
                                          <Chip
                                            icon={<RoomIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
                                            label={room.room_number}
                                            size="small"
                                            sx={{
                                              backgroundColor: isRoomSelected 
                                                ? 'rgba(76, 225, 182, 0.9)' 
                                                : 'rgba(0, 0, 0, 0.5)',
                                              color: '#fff',
                                              fontWeight: 700,
                                              fontSize: '0.85rem',
                                              height: 28,
                                              backdropFilter: 'blur(10px)',
                                              border: `1.5px solid ${isRoomSelected ? 'rgba(76, 225, 182, 1)' : 'rgba(255, 255, 255, 0.3)'}`,
                                              boxShadow: isRoomSelected 
                                                ? '0 4px 12px rgba(76, 225, 182, 0.4), 0 2px 4px rgba(0,0,0,0.3)' 
                                                : '0 2px 8px rgba(0,0,0,0.4)',
                                              letterSpacing: '0.5px',
                                              transition: 'all 0.3s ease',
                                              '& .MuiChip-icon': {
                                                color: '#fff !important',
                                                marginLeft: '8px',
                                              },
                                              '&:hover': {
                                                backgroundColor: isRoomSelected 
                                                  ? 'rgba(76, 225, 182, 1)' 
                                                  : 'rgba(0, 0, 0, 0.7)',
                                                transform: 'translateY(-1px)',
                                                boxShadow: isRoomSelected 
                                                  ? '0 6px 16px rgba(76, 225, 182, 0.5), 0 2px 4px rgba(0,0,0,0.3)' 
                                                  : '0 4px 12px rgba(0,0,0,0.5)',
                                              },
                                            }}
                                          />
                                        </Box>
                                          {holdReason ? (
                                            <Tooltip title={holdTooltip} arrow>
                                              <Chip
                                                label={holdLabel}
                                                size="small"
                                                sx={getCheckInRoomHoldOverlaySx(
                                                  getCheckInRoomHoldChipColor(holdReason),
                                                  isDark,
                                                )}
                                              />
                                            </Tooltip>
                                          ) : room.is_maintenance_blocked ? (
                                            <Chip
                                              label="Maintenance"
                                              size="small"
                                              sx={getCheckInRoomHoldOverlaySx('rgba(239, 83, 80, 0.92)', isDark)}
                                            />
                                          ) : isAllocatedForSelection && !isRoomSelected ? (
                                            <Chip
                                              label="Allocated"
                                              size="small"
                                              sx={getCheckInRoomHoldOverlaySx('rgba(244, 67, 54, 0.85)', isDark)}
                                            />
                                          ) : null}
                                      </Box>
                                      
                                      {/* Bottom Section - Room Type Name */}
                                      <Box 
                                        sx={{ 
                                          p: 1.25,
                                          backgroundColor: isDark ? 'rgba(42, 42, 49, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                                          backdropFilter: 'blur(10px)',
                                          borderTop: `1px solid ${isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)'}`,
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: isDark ? drawerTheme.text : '#333',
                                            fontSize: '0.75rem',
                                            fontWeight: 500,
                                            display: 'block',
                                            textAlign: 'center',
                                          }}
                                        >
                                          {roomTypeGroup.room_type.name}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </CardContent>
                                </Card>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                !loading && (
                  <Alert severity="info">
                    No available rooms found for this booking's date range.
                  </Alert>
                )
              )}

            </>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 1.5,
            borderTop: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
            backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
            display: 'flex',
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            fullWidth
            onClick={onClose}
            disabled={submitting}
            sx={{
              borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
              color: isDark ? drawerTheme.text : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
              '&:hover': {
                borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5',
              },
              '&:focus': {
                borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                outline: 'none',
              },
              '&:focus-visible': {
                borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                outline: 'none',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={handleAssignRooms}
            disabled={
              submitting ||
              (isChangeRoomMode ? !hasSelectedRooms : !(allRoomsSelected || hasUnallocationChange))
            }
            sx={{
              backgroundColor: CUSTOM_BORDER_COLOR,
              color: '#000',
              '&:hover': {
                backgroundColor: '#3ab896',
              },
              '&:disabled': {
                backgroundColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
                color: isDark ? drawerTheme.subtleText : '#666',
              },
            }}
          >
            {submitting ? <CircularProgress size={20} /> : (isChangeRoomMode ? 'Change' : 'Assign Rooms')}
          </Button>
        </Box>

        {/* Error Popup - Inside drawer */}
        {error && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: isDark ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.5)',
              zIndex: 1400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
            onClick={() => setError(null)}
          >
            <Paper
              onClick={(e) => e.stopPropagation()}
              sx={{
                backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
                borderRadius: 2,
                boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.2)',
                minWidth: 400,
                maxWidth: 500,
                p: 3,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: isDark ? drawerTheme.text : '#333',
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                Error
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: isDark ? drawerTheme.subtleText : '#666',
                  mb: 3,
                }}
              >
                {error}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  onClick={() => setError(null)}
                  variant="contained"
                  sx={{
                    backgroundColor: '#f44336',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: '#d32f2f',
                    },
                  }}
                >
                  OK
                </Button>
              </Box>
            </Paper>
          </Box>
        )}
        {canViewBookingAudit && (
          <BookingAuditDrawer
            open={auditDrawerOpen}
            onClose={() => setAuditDrawerOpen(false)}
            bookingId={auditBookingId}
            bookingCode={booking?.code || booking?.booking_code}
            isDark={isDark}
          />
        )}
      </Box>
    </Drawer>
  );
};

export default RoomAllocationDrawer;

